import { useCallback, useEffect } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useCommandInteract } from '../core/commands/commandInteract';
import { GROUND_PLANE } from '../core/scene';
import { entityMeshRegistry } from './toolEventBus';
import { useAppStore } from '../state/store';
import { constrainDraftPoint } from '../tools/snapping';

/**
 * CommandInteractScope — the viewport half of the generic command engine.
 *
 * Mounted inside every workbench canvas (SceneContent + BimViewport).
 * While a command interaction is active it:
 *   - captures pointer moves and publishes the ground-plane cursor,
 *   - captures clicks and submits the ground-plane point,
 *   - blocks other pointer consumers (tools see interaction-inactive state),
 *   - renders the command's live preview node.
 * When idle it renders nothing and touches no events.
 */
export function CommandInteractScope() {
    const { raycaster, pointer, camera, gl } = useThree();
    const active = useCommandInteract((s) => s.active);
    const spec = useCommandInteract((s) => s.spec);
    const collected = useCommandInteract((s) => s.collected);
    const cursor = useCommandInteract((s) => s.cursor);
    const stepIndex = useCommandInteract((s) => s.stepIndex);
    const setCursor = useCommandInteract((s) => s.setCursor);
    const addPoint = useCommandInteract((s) => s.addPoint);
    const addPick = useCommandInteract((s) => s.addPick);
    const { scene } = useThree();

    // Escape cancels the active interaction globally while one runs.
    useEffect(() => {
        if (!active) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') useCommandInteract.getState().cancelActive();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active]);

    const groundAt = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        // CPlane-aware picking (3D): when the workbench set a work
        // plane (CPLANE / AutoCPlane face pick), command points land on it
        // instead of the fixed world ground plane.
        const wp = useAppStore.getState().view.workPlane;
        if (wp && wp.visible) {
            const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
                new THREE.Vector3(wp.normal[0], wp.normal[1], wp.normal[2]).normalize(),
                new THREE.Vector3(wp.origin[0], wp.origin[1], wp.origin[2])
            );
            if (raycaster.ray.intersectPlane(plane, target)) return target;
        }
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /** Walk up the object chain to find a registry entity id. */
    const resolvePickedId = useCallback((obj: THREE.Object3D | null): string | null => {
        let cur: THREE.Object3D | null = obj;
        while (cur) {
            const id = cur.userData?.entityId as string | undefined;
            if (id) return id;
            cur = cur.parent;
        }
        return null;
    }, []);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!active) return;
        // Middle button is reserved for the 2D/3D-style pan gesture;
        // it must never submit a command point or pick while a command runs.
        if (e.button === 1) return;
        const step = spec?.steps[stepIndex];
        if (!step) return;
        if (step.kind === 'point') {
            e.stopPropagation();
            const p = groundAt();
            // Command points obey the same drafting constraints as drawing
            // tools: grid/polar/osnap/ortho against the last collected point.
            if (p) {
                const base = collected.length > 0 ? collected[collected.length - 1] : null;
                addPoint(constrainDraftPoint(p, camera, gl, base));
            }
            return;
        }
        if (step.kind === 'pick') {
            // Raycast the real scene for an entity root (userData.entityId
            // written by EntityLayer / overlay / reference layers).
            const hitObj = e.intersections.map((i) => i.object).find((o) => resolvePickedId(o))
                ?? raycastEntities(e.nativeEvent);
            const id = hitObj ? resolvePickedId(hitObj) : null;
            if (!id) return;
            if (step.acceptTypes && step.acceptTypes.length > 0) {
                const ent = useAppStore.getState().entities[id];
                if (!ent || !step.acceptTypes.includes(ent.type)) return;
            }
            e.stopPropagation();
            addPick(id);
        }
    }, [active, spec, stepIndex, groundAt, addPoint, addPick, scene, raycaster, camera, gl, collected, resolvePickedId]);

    /** Fallback manual raycast (R3F only raycasts objects with handlers). */
    function raycastEntities(native: PointerEvent): THREE.Object3D | null {
        const canvas = document.querySelector('canvas');
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((native.clientX - rect.left) / rect.width) * 2 - 1,
            -((native.clientY - rect.top) / rect.height) * 2 + 1
        );
        raycaster.setFromCamera(ndc, camera);
        const objs = Array.from(entityMeshRegistry.values());
        const hits = raycaster.intersectObjects(objs, true);
        for (const h of hits) {
            const id = resolvePickedId(h.object);
            if (id) return h.object;
        }
        return null;
    }

    const handlePointerMove = useCallback(() => {
        if (!active) return;
        const p = groundAt();
        if (!p) return;
        // Freehand capture: every move point accumulates (raw, unsnapped).
        if (spec?.collectOnMove) addPoint(p);
        // The rubber-band preview tracks the same constrained point a click
        // would commit, so what the user sees is what they get.
        const base = collected.length > 0 ? collected[collected.length - 1] : null;
        setCursor(constrainDraftPoint(p, camera, gl, base));
    }, [active, groundAt, setCursor, spec, addPoint, camera, gl, collected]);

    if (!active || !spec) return null;

    return (
        <group>
            {/* Invisible catch plane so the scope owns pointer events while a
                command interaction is active (matches the BIM event plane). */}
            <mesh
                visible={false}
                position={[0, 0, 0]}
                rotation={[0, 0, 0]}
                userData={{ isCommandPlane: true }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
            >
                <planeGeometry args={[10000, 10000]} />
            </mesh>
            {spec.preview?.(collected, cursor) ?? null}
        </group>
    );
}
